import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { 
  LayoutDashboard, 
  Link as LinkIcon, 
  TrendingUp, 
  CreditCard, 
  User, 
  HelpCircle, 
  LogOut,
  ShoppingCart,
  GraduationCap,
  Users,
  UserCircle,
  FileText,
  DollarSign,
  Hash,
  UsersRound,
  BarChart3,
  MapPin,
  Building2,
  LinkIcon as SecondaryLinkIcon,
  Share2,
  ChevronDown,
  Star,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { userService } from "../../_services";
import { cn } from "@/lib/utils";
import GoPro from "../Consumer/GoPro";
import GoProConfirmation from "../Consumer/GoProConfirmation";
import './sidebar-custom.css';

// Commission Tier Popup Component
function CommissionTierPopup({ isOpen, onClose, tierData, tierInfo, percentage }) {
  if (!tierInfo || !tierData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>NuLifeSpan Rx RockStar Revenue Program</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {tierData.tierIcon && (
              <img src={tierData.tierIcon} alt="" className="w-8 h-8" />
            )}
            <span className="font-semibold">{tierInfo.tierName}</span>
          </div>
          <div className="space-y-2">
            <Progress value={percentage} className="h-2" />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{tierData.from}</span>
              <span>{tierInfo.claims} Transaction{tierInfo.claims > 1 ? 's' : ''}</span>
              <span>{tierData.to}</span>
            </div>
          </div>
          <p className="text-sm">
            {tierData.to - tierInfo.claims + 1} Transaction{tierInfo.claims > 1 ? 's' : ''} to reach the next level
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AppSidebar({ isAdmin, isConsumer }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  
  const [commissionTier, setCommissionTier] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [percentage, setPercentage] = useState(0);
  const [showTraining, setShowTraining] = useState(false);
  const [showTierPopup, setShowTierPopup] = useState(false);
  const [showGoProModal, setShowGoProModal] = useState(false);
  const [showGoProConfirmation, setShowGoProConfirmation] = useState(false);

  const userDetail = window.atob(localStorage.getItem('hacker_user_detail') || ':');
  const [, fullName] = userDetail.split(':');
  const isDistributor = !isAdmin && !isConsumer;

  // Fetch commission tier data for distributors
  // Removed non-existent API calls:
  // - Get_UserTierProperties (doesn't exist in backend)
  // - TrainingModuleAccessible (doesn't exist in backend)

  const handleLogout = () => {
    userService.logout();
    navigate('/signup');
  };

  const handleNavClick = () => {
    setOpenMobile(false);
  };

  const openGoProModal = () => {
    setShowGoProModal(true);
  };

  const closeGoProModal = () => {
    setShowGoProModal(false);
  };

  const openConfirmationModal = () => {
    setShowGoProModal(false);
    setShowGoProConfirmation(true);
  };

  const closeConfirmationModal = () => {
    setShowGoProConfirmation(false);
  };

  // Admin navigation items
  const adminNavItems = [
    { path: '/Admin/groups', label: 'Group Numbers', icon: UsersRound },
    { path: '/Admin/training', label: 'Training', icon: GraduationCap },
    { path: '/Admin/users', label: 'Users', icon: UserCircle },
    { path: '/Admin/report', label: 'Reports', icon: FileText },
    { path: '/Admin/transactions', label: 'Transactions', icon: TrendingUp },
    { path: '/Admin/pricing', label: 'Pricing Tool', icon: DollarSign },
    { path: '/Admin/referral', label: 'Referral Links', icon: Share2 },
    { path: '/Admin/secondary-link', label: 'Secondary Link', icon: SecondaryLinkIcon },
  ];

  const analyticsItems = [
    { path: '/Admin/analytics/geographic', label: 'Geographic', icon: MapPin },
    { path: '/Admin/analytics/pharmacies', label: 'Pharmacies', icon: Building2 },
  ];

  // User/Distributor navigation items
  const userNavItems = [
    { path: '/Summary', label: 'Summary', icon: LayoutDashboard, show: isDistributor },
    { path: '/training', label: 'Training', icon: GraduationCap, show: isDistributor },
    { path: '/ref', label: 'Affiliate Info', icon: LinkIcon, show: isDistributor },
    { path: '/dashboard', label: 'Claims Dashboard', icon: TrendingUp, show: isDistributor },
    { path: '/cards', label: 'Marketing Materials', icon: CreditCard, show: isDistributor },
    { path: '/nls-rx-marketplace', label: 'NLS Rx Marketplace', icon: ShoppingCart, show: isDistributor },
    { path: '/account', label: 'Account Info', icon: User, show: true },
    { path: '/faq', label: "FAQ's", icon: HelpCircle, show: true },
  ];

  // Consumer navigation items
  const consumerNavItems = [
    { path: '/consumer/Summary', label: 'Summary', icon: LayoutDashboard },
    { path: '/consumer/share-card', label: 'Share Card', icon: Share2 },
    { path: '/consumer/faq', label: "Cashback FAQ's", icon: HelpCircle },
    { path: '/consumer/training', label: 'Training', icon: GraduationCap },
  ];

  const navItems = isAdmin ? adminNavItems : isConsumer ? consumerNavItems : userNavItems.filter(item => item.show);

  return (
    <>
      <Sidebar collapsible="icon" className="border-none pt-16">
        {/* Commission Tier Section for Distributors */}
        {commissionTier && tierData && isDistributor && (
          <SidebarGroup>
            <div className="tier-card space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {tierData.tierIcon && (
                    <img src={tierData.tierIcon} alt="" className="w-6 h-6" />
                  )}
                  <span className="text-sm font-semibold text-brand-dark">{commissionTier.tierName}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-brand-dark"
                  onClick={() => setShowTierPopup(true)}
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-1">
                <div className="progress-bar-bg">
                  <div 
                    className="progress-bar-fill transition-all duration-300" 
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-brand-dark">
                  <span>{tierData.from}</span>
                  <span>{commissionTier.claims} Transaction{commissionTier.claims > 1 ? 's' : ''}</span>
                  <span>{tierData.to}</span>
                </div>
              </div>
              <p className="text-xs text-brand-dark">
                {tierData.to - commissionTier.claims + 1} to next level
              </p>
            </div>
          </SidebarGroup>
        )}

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <React.Fragment key={item.path}>
                      <SidebarMenuItem>
                        <SidebarMenuButton 
                          asChild 
                          isActive={isActive}
                          onClick={handleNavClick}
                          className={`px-4 py-2.5 text-[15px] font-medium ${isActive ? 'text-brand-accent bg-brand-active-bg' : 'text-brand-dark bg-transparent'}`}
                        >
                          <Link to={item.path} className="flex items-center gap-3">
                            <Icon className={`h-5 w-5 ${isActive ? 'text-brand-accent' : 'text-brand-dark'}`} />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      
                      {/* Analytics submenu appears right after Transactions */}
                      {isAdmin && item.path === '/Admin/transactions' && (
                        <Collapsible defaultOpen={false}>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                className="group cursor-pointer hover:bg-sidebar-accent rounded-md flex items-center justify-between text-brand-dark text-[15px] font-medium py-2.5 px-4 w-full"
                              >
                                <div className="flex items-center gap-3">
                                  <BarChart3 className="h-5 w-5 text-brand-dark" />
                                  <span>Analytics</span>
                                </div>
                                <ChevronDown className="h-4 w-4 text-brand-dark transition-transform duration-200 group-data-[state=open]:rotate-180" />
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {analyticsItems.map((subItem) => {
                                const SubIcon = subItem.icon;
                                const isSubActive = location.pathname === subItem.path;
                                
                                return (
                                  <SidebarMenuSubItem key={subItem.path}>
                                    <SidebarMenuSubButton 
                                      asChild 
                                      isActive={isSubActive}
                                      onClick={handleNavClick}
                                      className={`px-4 py-2 text-sm font-medium ${isSubActive ? 'text-brand-accent bg-brand-active-bg' : 'text-brand-dark bg-transparent'}`}
                                    >
                                      <Link to={subItem.path} className="flex items-center gap-3">
                                        <SubIcon className={`h-4 w-4 ${isSubActive ? 'text-brand-accent' : 'text-brand-dark'}`} />
                                        <span>{subItem.label}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </React.Fragment>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Go Pro Button for Consumers */}
          {isConsumer && (
            <SidebarGroup>
              <SidebarGroupContent className="px-4">
                <Button 
                  onClick={openGoProModal}
                  className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold py-2.5 rounded-md shadow-md transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Star className="h-5 w-5 fill-white" />
                  <span>Go Pro</span>
                </Button>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter className="border-t border-brand-light-1">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={handleLogout}
                className="px-4 py-2.5 text-[15px] font-medium text-brand-dark"
              >
                <LogOut className="h-5 w-5 text-brand-dark" />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Commission Tier Popup */}
      <CommissionTierPopup
        isOpen={showTierPopup}
        onClose={() => setShowTierPopup(false)}
        tierData={tierData}
        tierInfo={commissionTier}
        percentage={percentage}
      />

      {/* Go Pro Modal */}
      {showGoProModal && (
        <GoPro 
          open={showGoProModal} 
          onClose={closeGoProModal}
          openConfirmationModal={openConfirmationModal}
        />
      )}

      {/* Go Pro Confirmation Modal */}
      {showGoProConfirmation && (
        <GoProConfirmation 
          open={showGoProConfirmation} 
          onClose={closeConfirmationModal}
        />
      )}
    </>
  );
}
